import React from 'react';
import type { ModalKey, ModalProps, ModalResult } from './modalRegistry';

/**
 * A single entry in the modal stack.
 *
 * Each stack item represents one active modal workflow.
 * It owns the Promise resolution functions associated with `open()`.
 */
type StackItem<K extends ModalKey = ModalKey> = {
  /** Unique identifier for the stack entry */
  id: string;

  /** Modal key used to resolve the component from the registry */
  key: K;

  /** Props passed to the modal via `open(key, props)` */
  props: ModalProps<K>;

  /** Resolves the Promise returned by `open()` */
  resolve: (value: ModalResult<K>) => void;

  /** Rejects the Promise returned by `open()` */
  reject: (reason?: unknown) => void;
};

/**
 * Value exposed by {@link ModalStackContext}.
 *
 * This defines the public API for interacting with the modal stack.
 */
type ModalStackContextValue = {
  /**
   * Current modal stack.
   *
   * The top-most item (last element) is the active modal.
   */
  stack: StackItem[];

  /**
   * Open a new modal and push it onto the stack.
   *
   * Returns a Promise that resolves or rejects when the modal completes.
   *
   * @param key Modal key to open
   * @param props Props for the modal
   */
  open: <K extends ModalKey>(key: K, props: ModalProps<K>) => Promise<ModalResult<K>>;

  /**
   * Close the top-most modal without resolving.
   *
   * This is treated as a cancellation.
   */
  closeTop: () => void;

  /**
   * Resolve the top-most modal successfully.
   *
   * This resolves the Promise returned by `open()` and
   * removes the modal from the stack.
   *
   * @param value Result value for the modal
   */
  resolveTop: (value: any) => void;

  /**
   * Reject the top-most modal.
   *
   * This rejects the Promise returned by `open()` and
   * removes the modal from the stack.
   *
   * @param reason Optional rejection reason
   */
  rejectTop: (reason?: unknown) => void;

  /**
   * Replace the top-most modal with another modal.
   *
   * Useful for multi-step workflows where the UI should
   * transition without stacking additional modals.
   *
   * @param key Modal key to replace with
   * @param props Props for the new modal
   */
  replaceTop: <K extends ModalKey>(key: K, props: ModalProps<K>) => Promise<ModalResult<K>>;
};

/**
 * React context backing the modal stack.
 *
 * This is intentionally private; consumers should use {@link useModalStack}.
 */
const ModalStackContext = React.createContext<ModalStackContextValue | null>(null);

/**
 * Hook for accessing the modal stack API.
 *
 * @throws Error if used outside of {@link ModalStackProvider}
 */
export const useModalStack = () => {
  const ctx = React.useContext(ModalStackContext);
  if (!ctx) {
    throw new Error('useModalStack must be used within ModalStackProvider');
  }
  return ctx;
};

/**
 * Generates a unique identifier for stack entries.
 *
 * Used only for internal bookkeeping/debugging.
 */
const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * ModalStackProvider
 *
 * Architectural role:
 * - Owns the modal stack state
 * - Implements the promise-based modal workflow API
 * - Provides stack manipulation functions to consumers
 *
 * This provider must wrap the application once, near the root.
 */
export const ModalStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  /**
   * Internal modal stack.
   *
   * Stack order:
   * - Earlier items are deeper in the stack
   * - The last item is the active modal
   */
  const [stack, setStack] = React.useState<StackItem[]>([]);

  /**
   * Open a new modal.
   *
   * Pushes a new stack item and returns a Promise that will be
   * resolved or rejected by the modal itself.
   */
  const open = React.useCallback(<K extends ModalKey>(key: K, props: ModalProps<K>) => {
    return new Promise<ModalResult<K>>((resolve, reject) => {
      const item: StackItem<K> = {
        id: makeId(),
        key,
        props,
        resolve,
        reject,
      };

      setStack((prev) => [...prev, item]);
    });
  }, []);

  /**
   * Close the top-most modal without resolving its Promise.
   *
   * This is typically used for user cancellation via backdrop
   * click or close button.
   */
  const closeTop = React.useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  /**
   * Resolve the top-most modal successfully.
   *
   * IMPORTANT:
   * - The Promise is resolved BEFORE the modal is removed.
   * - This guarantees callers receive the result synchronously
   *   with stack updates.
   */
  const resolveTop = React.useCallback((value: any) => {
    setStack((prev) => {
      const top = prev[prev.length - 1];
      if (!top) return prev;

      // Resolve before removing from the stack
      (top.resolve as any)(value);

      return prev.slice(0, -1);
    });
  }, []);

  /**
   * Reject the top-most modal.
   *
   * Used when the modal is cancelled or errors.
   */
  const rejectTop = React.useCallback((reason?: unknown) => {
    setStack((prev) => {
      const top = prev[prev.length - 1];
      if (!top) return prev;

      top.reject(reason);

      return prev.slice(0, -1);
    });
  }, []);

  /**
   * Replace the top-most modal with a new modal.
   *
   * This is functionally equivalent to:
   * - closing the current modal
   * - immediately opening another
   *
   * but without intermediate UI flicker or stack depth changes.
   */
  const replaceTop = React.useCallback(<K extends ModalKey>(key: K, props: ModalProps<K>) => {
    return new Promise<ModalResult<K>>((resolve, reject) => {
      setStack((prev) => {
        const next = prev.slice(0, -1);
        const item: StackItem<K> = {
          id: makeId(),
          key,
          props,
          resolve,
          reject,
        };

        return [...next, item];
      });
    });
  }, []);

  /**
   * Context value exposed to consumers.
   */
  const value: ModalStackContextValue = {
    stack,
    open,
    closeTop,
    resolveTop,
    rejectTop,
    replaceTop,
  };

  return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
};
